"use client";

import { ReactNode } from "react";
import { useWebWorkerActionButtons } from "../../../hooks/useWebWorker";
import { BlockData, ClientSiteData, PageType } from "../../../types/site";
import { PageContext } from "../../../types/ui";
import { IVDPPageBlock, VDPBlockGlobalInitData, VDPBlockPosition } from "../../../types/vdp";
import { Vehicle } from "../../../types/vehicle";
import { useIsMobile } from "../../../utils/useIsMobile";
import {
  getVdpBlockDataForComponent,
  getVdpGlobalInitHookForComponent,
} from "../../vdp-block-components/vdpComponentFactory";
import styles from "./vehicle-component.module.scss";
import { Translations } from "../../../utils/translations";
import { VehicleConditionalCardData } from "../../../types/blocks";
import OfferPanel from "./offer-panel";

interface IVehicleCompoenent {
  siteData: ClientSiteData;
  pageContext: PageContext;
  currentVehicle: Vehicle;
  vehicleCardData: VehicleConditionalCardData;
  vdpBlocks: BlockData[];
  children: ReactNode[];
  translations: Translations;
}

interface VDPComponentWithData {
  block: BlockData;
  component: (props: IVDPPageBlock, key?: any) => JSX.Element;
}

function createVdpBlockComponents(
  blocks: VDPComponentWithData[],
  renderingPosition: VDPBlockPosition,
  siteData: ClientSiteData,
  pageContext: PageContext,
  vdpGlobalData: VDPBlockGlobalInitData,
  translations: Translations,
): (JSX.Element | null)[] {
  return blocks.map((item, index) => {
    return item.component(
      {
        config: item.block.data,
        pageContext: pageContext,
        siteData: siteData,
        renderingPosition: renderingPosition,
        vdpGlobalData,
        translations,
      },
      index,
    );
  });
}

export default function VehicleComponent({
  siteData,
  pageContext,
  currentVehicle,
  vehicleCardData,
  vdpBlocks,
  translations,
  children,
}: IVehicleCompoenent) {
  const isMobile = useIsMobile(768, pageContext.isSmartphone);

  const vdpGlobalData: VDPBlockGlobalInitData = {
    actionButtons: [],
    isSingleColumnRendering: isMobile,
    vehicle: currentVehicle,
    vehicleCardData: vehicleCardData,
  };

  const leftBlocks: VDPComponentWithData[] = [];
  const rightBlocks: VDPComponentWithData[] = [];
  const bottomBlocks: VDPComponentWithData[] = [];
  vdpBlocks.forEach((block) => {
    const vdpBlockData = getVdpBlockDataForComponent(block.componentName);
    if (!vdpBlockData) {
      return;
    }

    const globalInitHook = getVdpGlobalInitHookForComponent(block.componentName);
    if (globalInitHook) {
      globalInitHook(block.data, currentVehicle, vdpGlobalData);
    }
    if (vdpBlockData.position === VDPBlockPosition.LEFT) {
      leftBlocks.push({ block, component: vdpBlockData.component });
    } else if (vdpBlockData.position === VDPBlockPosition.RIGHT) {
      leftBlocks.push({ block, component: vdpBlockData.component }); // it is not mistake right blocks can insert some data to left blocks
      rightBlocks.push({ block, component: vdpBlockData.component });
    } else if (vdpBlockData.position === VDPBlockPosition.BOTTOM) {
      bottomBlocks.push({ block, component: vdpBlockData.component });
    }
  });

  vdpGlobalData.actionButtons = useWebWorkerActionButtons(
    vdpGlobalData.actionButtons,
    PageType.VDP,
    "vdp",
    pageContext.host,
    pageContext.locale,
  );

  return (
    <>
      <OfferPanel
        vehicle={currentVehicle}
        siteData={siteData}
        isSmartphone={pageContext.isSmartphone}
        translations={translations}
      />
      <main className={styles.main}>
        <section className={styles.wrapper}>
          <article className={styles.container}>
            <div className={styles.content}>
              {createVdpBlockComponents(
                leftBlocks,
                VDPBlockPosition.LEFT,
                siteData,
                pageContext,
                vdpGlobalData,
                translations,
              )}
              {!isMobile &&
                createVdpBlockComponents(
                  bottomBlocks,
                  VDPBlockPosition.BOTTOM,
                  siteData,
                  pageContext,
                  vdpGlobalData,
                  translations,
                )}
            </div>
            {!isMobile /* right column */ && (
              <div className={styles.sidebar}>
                {createVdpBlockComponents(
                  rightBlocks,
                  VDPBlockPosition.RIGHT,
                  siteData,
                  pageContext,
                  vdpGlobalData,
                  translations,
                )}
              </div>
            )}
          </article>
          {isMobile &&
            createVdpBlockComponents(
              bottomBlocks,
              VDPBlockPosition.BOTTOM,
              siteData,
              pageContext,
              vdpGlobalData,
              translations,
            )}
          {children}
        </section>
      </main>
    </>
  );
}
